import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Spinner } from 'react-bootstrap';
import { FaBuilding, FaUsers, FaMapMarkerAlt, FaClipboardCheck, FaFileAlt, FaPlus } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import superadminAPI from '../../utils/superadminAPI';

const SuperAdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalCompanies: 0,
    totalPersonnel: 0,
    totalSites: 0,
    totalCheckIns: 0,
    totalReports: 0,
    recentCompanies: []
  });
  const navigate = useNavigate();

  useEffect(() => {
    // Add debugging to see what's happening with authentication
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    
    console.log('SuperAdmin Dashboard - Token exists:', !!token);
    console.log('SuperAdmin Dashboard - User data:', user ? JSON.parse(user) : 'No user data');
    
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await superadminAPI.getDashboardStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      
      // Handle specific error cases
      if (error.response) {
        if (error.response.status === 401) {
          console.log('Authentication error - checking token...');
          // Don't redirect immediately, let the user see the error
          // The PrivateRoute will handle authentication if needed
        } else if (error.response.status === 403) {
          console.log('Access denied - insufficient permissions');
        } else if (error.response.status >= 500) {
          console.log('Server error - please try again later');
        }
      }
      
      // Set default stats to prevent UI errors
      setStats({
        totalCompanies: 0,
        totalPersonnel: 0,
        totalSites: 0,
        totalCheckIns: 0,
        totalReports: 0,
        recentCompanies: []
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container fluid className="py-3 py-md-4">
      <Row className="mb-3 mb-md-4">
        <Col xs={12} md={8}>
          <h1 className="mb-2 mb-md-4">Super Admin Dashboard</h1>
          <p className="text-muted mb-0">Manage companies, personnel, and sites across the FieldFlow platform.</p>
        </Col>
        <Col xs={12} md={4} className="d-flex align-items-center justify-content-start justify-content-md-end mt-3 mt-md-0">
          <Button 
            variant="primary" 
            className="d-flex align-items-center w-100 w-md-auto"
            onClick={() => navigate('/superadmin/company-admins/new')}
          >
            <FaPlus className="me-2" /> Add Company
          </Button>
        </Col>
      </Row>

      {/* Stats Cards */}
      <Row className="mb-3 mb-md-4">
        <Col xs={12} sm={6} lg={3} className="mb-3">
          <Card className="h-100 shadow-sm">
            <Card.Body className="d-flex align-items-center">
              <div className="rounded-circle bg-primary bg-opacity-10 p-2 p-md-3 me-2 me-md-3">
                <FaBuilding className="text-primary" size={20} />
              </div>
              <div>
                <h6 className="text-muted mb-1 small">Total Companies</h6>
                <h3 className="mb-0 h4">{stats.totalCompanies}</h3>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={3} className="mb-3">
          <Card className="h-100 shadow-sm">
            <Card.Body className="d-flex align-items-center">
              <div className="rounded-circle bg-success bg-opacity-10 p-2 p-md-3 me-2 me-md-3">
                <FaUsers className="text-success" size={20} />
              </div>
              <div>
                <h6 className="text-muted mb-1 small">Total Personnel</h6>
                <h3 className="mb-0 h4">{stats.totalPersonnel}</h3>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={3} className="mb-3">
          <Card className="h-100 shadow-sm">
            <Card.Body className="d-flex align-items-center">
              <div className="rounded-circle bg-warning bg-opacity-10 p-2 p-md-3 me-2 me-md-3">
                <FaMapMarkerAlt className="text-warning" size={20} />
              </div>
              <div>
                <h6 className="text-muted mb-1 small">Total Sites</h6>
                <h3 className="mb-0 h4">{stats.totalSites}</h3>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={3} className="mb-3">
          <Card className="h-100 shadow-sm">
            <Card.Body className="d-flex align-items-center">
              <div className="rounded-circle bg-info bg-opacity-10 p-2 p-md-3 me-2 me-md-3">
                <FaClipboardCheck className="text-info" size={20} />
              </div>
              <div>
                <h6 className="text-muted mb-1 small">Total Check-ins</h6>
                <h3 className="mb-0 h4">{stats.totalCheckIns}</h3>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-3 mb-md-4">
        <Col xs={12} sm={6} className="mb-3">
          <Card className="h-100 shadow-sm">
            <Card.Body className="d-flex align-items-center">
              <div className="rounded-circle bg-danger bg-opacity-10 p-2 p-md-3 me-2 me-md-3">
                <FaFileAlt className="text-danger" size={20} />
              </div>
              <div>
                <h6 className="text-muted mb-1 small">Total Reports</h6>
                <h3 className="mb-0 h4">{stats.totalReports}</h3>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Recent Companies */}
      <Row className="mb-3 mb-md-4">
        <Col>
          <Card className="shadow-sm">
            <Card.Header className="d-flex justify-content-between align-items-center bg-white flex-column flex-md-row">
              <h5 className="mb-0 mb-md-0">Recently Added Companies</h5>
              <Button 
                variant="outline-primary" 
                size="sm"
                as={Link}
                to="/superadmin/company-admins"
                className="mt-2 mt-md-0"
              >
                View All
              </Button>
            </Card.Header>
            <Card.Body>
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead>
                    <tr>
                      <th>Company Name</th>
                      <th className="d-none d-md-table-cell">Admin</th>
                      <th className="d-none d-lg-table-cell">Email</th>
                      <th className="d-none d-md-table-cell">Added On</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recentCompanies.length > 0 ? (
                      stats.recentCompanies.map((company) => (
                        <tr key={company.id}>
                          <td>
                            <div className="d-flex align-items-center">
                              <div className="rounded-circle bg-primary bg-opacity-10 p-1 p-md-2 me-2">
                                <FaBuilding className="text-primary" size={16} />
                              </div>
                              <div>
                                <div className="fw-bold">{company.company}</div>
                                <small className="text-muted d-md-none">{company.name}</small>
                              </div>
                            </div>
                          </td>
                          <td className="d-none d-md-table-cell">{company.name}</td>
                          <td className="d-none d-lg-table-cell">{company.email}</td>
                          <td className="d-none d-md-table-cell">{formatDate(company.createdAt)}</td>
                          <td>
                            <Button 
                              variant="outline-primary" 
                              size="sm"
                              as={Link}
                              to={`/superadmin/company-admins/${company.id}`}
                              className="w-100 w-md-auto"
                            >
                              View Details
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center">No companies found</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default SuperAdminDashboard;
